import { useEffect, useId, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import PlaceMap from './PlaceMap.jsx';

/**
 * Choose a place two ways: type and pick a geocoding suggestion, or drop it
 * straight onto the map. The second path matters here because trailhead car
 * parks and lay-by pickup points frequently have no searchable address.
 */
export default function PlacePicker({
  value,
  onChange,
  label,
  placeholder = 'Rechercher une adresse, un lieu...',
  required = false,
  hint,
  /** Other points worth seeing while placing this one: destination, departure... */
  contextMarkers = [],
  tiles,
}) {
  /**
   * Placing a point is this control's own business now. It used to be hoisted to
   * the page so the dialog could be hidden and the full-screen map used, which
   * meant the form vanished mid-edit. The map lives here instead, and grows when
   * it is needed.
   */
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const boxRef = useRef(null);
  const listId = useId();

  // Debounced search; each keystroke aborts the previous request.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 3) {
      setResults([]);
      setBusy(false);
      return undefined;
    }
    const controller = new AbortController();
    setBusy(true);
    const timer = setTimeout(() => {
      api
        .searchPlaces(term, controller.signal)
        .then(({ results: found }) => {
          setResults(found);
          setOpen(true);
          setCursor(-1);
        })
        .catch(() => {})
        .finally(() => setBusy(false));
    }, 420);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const choose = (place) => {
    onChange({ label: place.label, address: place.address, lat: place.lat, lng: place.lng });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const onKeyDown = (event) => {
    /**
     * Swallow Enter unconditionally. This field sits inside the outing form, so
     * letting the key through submits the whole thing; confirming a search is
     * never a request to create the outing.
     */
    if (event.key === 'Enter') {
      event.preventDefault();
      // Same rule as the map's search: the highlighted result, or the first one.
      if (open && results.length) choose(results[cursor >= 0 ? cursor : 0]);
      return;
    }
    if (!open || results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => (c + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => (c - 1 + results.length) % results.length);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const mapProps = { contextMarkers, tiles, picking, onPickingChange: setPicking, onChange };

  if (value) {
    return (
      <div className="field">
        {label && <span className="field-label">{label}{required && ' *'}</span>}
        <div className={`place-chosen ${value.lat == null ? 'pending' : ''}`}>
          <span className="dot" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="truncate" style={{ fontWeight: 600 }}>{value.label}</div>
            {value.address && value.address !== value.label && (
              <div className="truncate small faint">{value.address}</div>
            )}
          </div>
          <button
            type="button"
            className={`btn btn-sm ${picking ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPicking((p) => !p)}
          >
            {picking ? 'Terminer' : 'Deplacer'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            /* "Changer" means start over by name, so it also leaves placing mode:
               otherwise the search box never comes back and the only way left to
               choose is the map. */
            onClick={() => {
              setPicking(false);
              onChange(null);
            }}
          >
            Changer
          </button>
        </div>
        <PlaceMap value={value} {...mapProps} />
      </div>
    );
  }

  if (picking) {
    return (
      <div className="field">
        {label && <span className="field-label">{label}{required && ' *'}</span>}
        <div className="place-chosen pending">
          <span className="dot" />
          <span className="small" style={{ flex: 1 }}>Touchez la carte pour placer ce point.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPicking(false)}>
            Annuler
          </button>
        </div>
        <PlaceMap value={null} {...mapProps} />
      </div>
    );
  }

  return (
    <div className="field place-picker" ref={boxRef}>
      {label && (
        <label className="field-label" htmlFor={listId}>
          {label}{required && ' *'}
        </label>
      )}
      <div className="row gap-8">
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            id={listId}
            className="input"
            value={query}
            placeholder={placeholder}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length && setOpen(true)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={open}
            aria-controls={`${listId}-list`}
          />
          {busy && <span className="spinner" style={{ position: 'absolute', right: 10, top: 11 }} />}
        </div>
        <button type="button" className="btn btn-sm" onClick={() => setPicking(true)} title="Placer sur la carte">
          📍 Carte
        </button>
      </div>
      {hint && <span className="field-hint">{hint}</span>}

      {open && results.length > 0 && (
        <div className="place-results" id={`${listId}-list`} role="listbox">
          {results.map((place, i) => (
            <button
              type="button"
              key={`${place.lat},${place.lng},${i}`}
              className={`place-result ${i === cursor ? 'active' : ''}`}
              onClick={() => choose(place)}
              onMouseEnter={() => setCursor(i)}
              role="option"
              aria-selected={i === cursor}
            >
              <div className="place-result-title truncate">{place.label}</div>
              <div className="place-result-sub truncate">{place.address}</div>
            </button>
          ))}
        </div>
      )}
      {open && !busy && query.trim().length >= 3 && results.length === 0 && (
        <div className="place-results">
          <p className="small faint" style={{ padding: '8px 10px' }}>
            Aucun resultat. Placez le point sur la carte.
          </p>
        </div>
      )}
    </div>
  );
}
